// ABOUTME: The storm crown: a storm-grey copy of the crown tier, scaled up from its base, drawn over the lit crown on the impact screens.
// ABOUTME: It is decoration only (aria-hidden); the lit crown tier underneath keeps the words in the reading order.
import { Box } from '@mui/material';
import { keyframes } from '@emotion/react';
import type { CrossingWeatherPatternDto } from '../../../../types/api';
import { CROSSING_IMPACT_CROWN_SHADOW, CROSSING_IMPACT_CROWN_STORM } from '../../../../theme/crossingTokens';
import SculptedCloud from './SculptedCloud';
import { WeatherCopy } from './tierCopy';
import { TIERS } from './stackGeometry';
import { DISSOLVE_SEC } from '../impact/stormDissolve';

interface StormCrownProps {
  weather?: CrossingWeatherPatternDto;
  // The entrance: the storm crown fades in over the lit crown.
  fadeIn: boolean;
  // Forgive: the storm crown leaves with the rain.
  dissolving: boolean;
}

// The storm crown paints above the four tiers (z 2 to 5), the question cloud and the weather vignette (z 6) and the tier arrows (z 7).
export const STORM_CROWN_Z = 8;
const SCALE = 1.35;

const fadeInFrames = keyframes`
  to { opacity: 1; }
`;

const head = TIERS.find((t) => t.id === 'head')!;
const [x, y, w, h] = head.box;
// SculptedCloud places itself at its tier's box, so the wrapper sits on the head tier's box and the cloud inside sits at its origin.
const rebased = { ...head, box: [0, 0, w, h] as const };

export default function StormCrown({ weather, fadeIn, dissolving }: StormCrownProps) {
  const style = dissolving
    ? { opacity: 0, transition: `opacity ${DISSOLVE_SEC}s ease` }
    : fadeIn
      ? { opacity: 0, animationDuration: '1s', animationDelay: '1.3s', animationTimingFunction: 'ease' }
      : { opacity: 1 };

  return (
    <Box
      data-testid="storm-crown"
      aria-hidden="true"
      style={style}
      sx={{
        position: 'absolute', left: x, top: y, width: w, height: h, zIndex: STORM_CROWN_Z, pointerEvents: 'none',
        transform: `scale(${SCALE})`, transformOrigin: '50% 100%',
        filter: CROSSING_IMPACT_CROWN_SHADOW,
        ...(fadeIn && !dissolving ? { animationName: `${fadeInFrames}`, animationFillMode: 'forwards' } : {}),
      }}
    >
      <SculptedCloud tier={rebased} fill={CROSSING_IMPACT_CROWN_STORM} testId="storm-crown-body" instant>
        {weather && <WeatherCopy weather={weather} reveal={false} stormy />}
      </SculptedCloud>
    </Box>
  );
}
